import React, { useEffect, useState } from "react";
import { ToastContainer, toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { Container, Row, Col, Card, Form, Button, Table, Badge, Alert } from "react-bootstrap";
import { FaPlus, FaEdit, FaTrash, FaArrowLeft, FaSearch, FaBarcode, FaCheck, FaTimes } from "react-icons/fa";
import { useLectores } from "../../../services/hooks/useLectores";
import { useComedores } from "../../../services/hooks/useComedores";
import { useProyectos } from "../../../services/hooks/useProyectos";
import { Modal } from "antd";
import ModalLector from "../../../components/modals/ModalLector";

const CargarLectoras = () => {
  const navigate = useNavigate();

  // Usar los hooks personalizados
  const {
    lectores,
    loading: loadingLectores,
    error: errorLectores,
    createLector,
    updateLector,
    deleteLector: deleteLectorApi,
    fetchLectoresByProyecto,
    clearError: clearLectorError
  } = useLectores();

  const {
    comedores,
    fetchComedoresByProyecto,
    error: errorComedores,
    clearError: clearComedorError
  } = useComedores();

  const {
    proyectos,
    error: errorProyectos,
    clearError: clearProyectoError
  } = useProyectos();

  const [state, setState] = useState({
    lectorSeleccionado: null,
    filtro: "",
  });

  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // 'create' o 'edit'
  const [lectorToEdit, setLectorToEdit] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [lectorToDelete, setLectorToDelete] = useState(null);

  // Cargar lectores del proyecto al montar el componente
  useEffect(() => {
    const selectedProject = JSON.parse(localStorage.getItem('selectedProject'));
    if (selectedProject && selectedProject.id) {
      fetchLectoresByProyecto(selectedProject.id);
    }
  }, [fetchLectoresByProyecto]);

  // Limpiar errores cuando se monta el componente
  useEffect(() => {
    if (errorLectores) clearLectorError();
    if (errorComedores) clearComedorError();
    if (errorProyectos) clearProyectoError();
  }, [errorLectores, errorComedores, errorProyectos, clearLectorError, clearComedorError, clearProyectoError]);

  const handleSearch = (event) => {
    setState(prev => ({ ...prev, filtro: event.target.value }));
  };

  const handleLectorSelect = (lector) => {
    setState(prev => ({ 
      ...prev, 
      lectorSeleccionado: prev.lectorSeleccionado?.id === lector.id ? null : lector 
    }));
  };

  // Función para eliminar lector
  const handleDeleteLector = (lector) => {
    if (!lector) {
      toast.error("Por favor seleccione un lector para eliminar", {
        position: "bottom-right",
        autoClose: 2000
      });
      return;
    }
    setLectorToDelete(lector);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!lectorToDelete) return;

    // Cerrar modal inmediatamente
    setShowDeleteModal(false);
    
    try {
      // Eliminar en el backend - el hook ya maneja las notificaciones
      await deleteLectorApi(lectorToDelete.id);
      setLectorToDelete(null);
    } catch (error) {
      console.error("Error al eliminar lector:", error);
      // El error ya se maneja en el hook con toast
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setLectorToDelete(null);
  };

  const handleOpenCreateModal = async () => {
    // Cargar comedores solo cuando se abre el modal
    const selectedProject = JSON.parse(localStorage.getItem('selectedProject'));
    if (selectedProject && selectedProject.id) {
      await fetchComedoresByProyecto(selectedProject.id);
    }
    
    setModalMode('create');
    setLectorToEdit(null);
    setShowModal(true);
  };

  const handleOpenEditModal = async () => {
    if (state.lectorSeleccionado) {
      // Cargar comedores solo cuando se abre el modal
      const selectedProject = JSON.parse(localStorage.getItem('selectedProject'));
      if (selectedProject && selectedProject.id) {
        await fetchComedoresByProyecto(selectedProject.id);
      }
      
      setModalMode('edit');
      setLectorToEdit(state.lectorSeleccionado);
      setShowModal(true);
    } else {
      toast.error("Por favor seleccione un lector para editar", {
        position: "top-right",
        autoClose: 2000
      });
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
  };

  // Función para manejar el guardado del lector
  const handleSaveLector = async (lectorData) => {
    try {
      if (modalMode === 'create') {
        await createLector(lectorData);
      } else {
        await updateLector(lectorToEdit.id, lectorData);
      }
      
      // Cerrar el modal
      handleCloseModal();
    } catch (error) {
      console.error('Error al guardar lector:', error);
      // El error ya se maneja en el hook con toast
    }
  };

  // Filtrar lectores según el filtro
  const lectoresFiltrados = lectores.filter((lector) =>
    lector.nombre.toLowerCase().includes(state.filtro.toLowerCase())
  );

  return (
    <>
      {/* Digital Harmony Styles para Mantenimiento Lectoras */}
      <style>
        {`
          /* Fondo degradado */
          .dh-maintenance-bg {
            background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
            min-height: 100vh;
          }
          
          /* Header principal */
          .dh-main-header {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            padding: 2rem 0;
            margin-bottom: 2rem;
            border-radius: 0 0 30px 30px;
            box-shadow: 0 10px 30px rgba(102, 126, 234, 0.2);
          }
          
          .dh-main-title {
            font-size: 2.2rem;
            font-weight: 700;
            margin: 0;
          }
          
          .dh-main-subtitle {
            font-size: 1.1rem;
            opacity: 0.9;
            margin: 0.5rem 0 0 0;
          }
          
          /* Cards con Digital Harmony */
          .dh-card {
            border: none;
            border-radius: 20px;
            box-shadow: 0 15px 35px rgba(0,0,0,0.1);
            background: white;
          }
          
          .dh-card-header {
            border-radius: 20px 20px 0 0;
            padding: 1.5rem 2rem;
            border: none;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            font-weight: 700;
          }
          
          /* Botones Digital Harmony */
          .btn-dh-primary {
            background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%);
            border: none;
            color: white;
            border-radius: 12px;
            padding: 12px 20px;
            font-weight: 600;
            box-shadow: 0 4px 14px rgba(17, 153, 142, 0.3);
            transition: all 0.3s ease;
          }
          
          .btn-dh-primary:hover {
            background: linear-gradient(135deg, #38ef7d 0%, #2dd4bf 100%);
            transform: translateY(-2px);
            box-shadow: 0 8px 25px rgba(17, 153, 142, 0.4);
            color: white;
          }
          
          .btn-dh-warning {
            background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%);
            border: none;
            color: white;
            border-radius: 12px;
            padding: 12px 20px;
            font-weight: 600;
            box-shadow: 0 4px 14px rgba(251, 191, 36, 0.3);
            transition: all 0.3s ease;
          }
          
          .btn-dh-warning:hover {
            background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
            transform: translateY(-2px);
            box-shadow: 0 8px 25px rgba(251, 191, 36, 0.4);
            color: white;
          }
          
          .btn-dh-danger {
            background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
            border: none;
            color: white;
            border-radius: 12px;
            padding: 12px 20px;
            font-weight: 600;
            box-shadow: 0 4px 14px rgba(239, 68, 68, 0.3);
            transition: all 0.3s ease;
          }
          
          .btn-dh-danger:hover {
            background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
            transform: translateY(-2px);
            box-shadow: 0 8px 25px rgba(239, 68, 68, 0.4);
            color: white;
          }
          
          .btn-dh-secondary {
            background: linear-gradient(135deg, #6b7280 0%, #374151 100%);
            border: none;
            color: white;
            border-radius: 12px;
            padding: 12px 20px;
            font-weight: 600;
            box-shadow: 0 4px 14px rgba(107, 114, 128, 0.3);
            transition: all 0.3s ease;
          }
          
          .btn-dh-secondary:hover {
            background: linear-gradient(135deg, #374151 0%, #1f2937 100%);
            transform: translateY(-2px);
            box-shadow: 0 8px 25px rgba(107, 114, 128, 0.4);
            color: white;
          }
          
          /* Campos de formulario modernos */
          .dh-form-control {
            border: 2px solid #e5e7eb;
            border-radius: 10px;
            padding: 12px 16px;
            font-size: 1rem;
            transition: all 0.3s ease;
            background: rgba(255, 255, 255, 0.9);
          }
          
          .dh-form-control:focus {
            border-color: #667eea;
            box-shadow: 0 0 0 0.2rem rgba(102, 126, 234, 0.25);
            background: white;
          }
          
          /* Enhanced Table */
          .dh-enhanced-table {
            border-collapse: separate;
            border-spacing: 0;
            border-radius: 15px;
            overflow: hidden;
            box-shadow: 0 4px 15px rgba(0,0,0,0.05);
            background: white;
          }
          
          .dh-enhanced-table thead th {
            background: #374151 !important;
            color: white !important;
            font-weight: 700 !important;
            font-size: 0.875rem !important;
            text-transform: uppercase !important;
            padding: 1rem 0.75rem !important;
            border: none !important;
            position: sticky !important;
            top: 0 !important;
            z-index: 10 !important;
          }
          
          .dh-enhanced-table tbody td {
            padding: 0.75rem;
            border: none;
            border-bottom: 1px solid #f3f4f6;
            font-size: 0.9rem;
            vertical-align: middle;
          }
          
          .dh-enhanced-table tbody tr:hover {
            background: linear-gradient(135deg, #f9fafb 0%, rgba(102, 126, 234, 0.02) 100%) !important;
          }
          
          .dh-enhanced-table tbody tr.selected {
            background: linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%) !important;
            border-left: 4px solid #667eea;
          }
          
          /* Checkbox personalizado */
          .dh-checkbox {
            width: 20px;
            height: 20px;
            border: 2px solid #d1d5db;
            border-radius: 6px;
            background: white;
            cursor: pointer;
            transition: all 0.2s ease;
          }
          
          .dh-checkbox:checked {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            border-color: #667eea;
          }
          
          /* Badges mejorados */
          .dh-badge-success {
            background: linear-gradient(135deg, #10b981 0%, #059669 100%);
            color: white;
            border-radius: 10px;
            padding: 6px 12px;
            font-weight: 600;
            font-size: 0.85rem;
            display: inline-flex;
            align-items: center;
            gap: 0.5rem;
          }
          
          .dh-badge-danger {
            background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
            color: white;
            border-radius: 10px;
            padding: 6px 12px;
            font-weight: 600;
            font-size: 0.85rem;
            display: inline-flex;
            align-items: center;
            gap: 0.5rem;
          }
        `}
      </style>

      <div className="dh-maintenance-bg">
        <ToastContainer />
        
        <div className="dh-main-header">
          <Container>
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <h1 className="dh-main-title">
                  <FaBarcode className="me-3" />
                  Mantenimiento de Lectoras
                </h1>
                <p className="dh-main-subtitle">Gestión y administración de lectoras del sistema</p>
              </div>
              <Button 
                className="btn-dh-secondary d-flex align-items-center"
                onClick={() => navigate("/home")}
              >
                <FaArrowLeft className="me-2" />
                Volver
              </Button>
            </div>
          </Container>
        </div>

        <Container fluid className="py-4">
          <Row>
            <Col lg={12}>
              <Card className="dh-card">
                <Card.Header className="dh-card-header">
                  <div className="d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">Lista de Lectoras</h5>
                    <div className="d-flex gap-2">
                      <Button
                        onClick={handleOpenCreateModal}
                        className="btn-dh-primary d-flex align-items-center"
                      >
                        <FaPlus className="me-2" />
                        Nueva Lectora
                      </Button>
                      <Button
                        className="btn-dh-warning d-flex align-items-center"
                        onClick={handleOpenEditModal}
                      >
                        <FaEdit className="me-2" />
                        Editar
                      </Button>
                      <Button
                        className="btn-dh-danger d-flex align-items-center"
                        onClick={() => handleDeleteLector(state.lectorSeleccionado)}
                        disabled={!state.lectorSeleccionado}
                      >
                        <FaTrash className="me-2" />
                        Eliminar
                      </Button>
                    </div>
                  </div>
                </Card.Header>
                <Card.Body style={{ padding: '2rem' }}>
                  <div className="mb-4">
                    <div className="position-relative">
                      <Form.Control
                        type="search"
                        className="dh-form-control"
                        placeholder="Buscar lectora..."
                        value={state.filtro}
                        onChange={handleSearch}
                        style={{ paddingLeft: '3rem' }}
                      />
                      <FaSearch 
                        className="position-absolute" 
                        style={{ 
                          left: '1rem', 
                          top: '50%', 
                          transform: 'translateY(-50%)', 
                          color: '#6b7280' 
                        }} 
                      />
                    </div>
                  </div>

                  <div className="table-responsive" style={{ maxHeight: '600px', overflowY: 'auto' }}>
                    <Table className="dh-enhanced-table">
                      <thead>
                        <tr>
                          <th style={{ width: "50px" }}></th>
                          <th>Nombre</th>
                          <th className="text-center">Estado</th>
                        </tr>
                      </thead>
                      <tbody>
                        {loadingLectores ? (
                          <tr>
                            <td colSpan="3" className="text-center py-5">
                              <div className="d-flex flex-column align-items-center">
                                <div className="spinner-border text-primary mb-3" role="status">
                                  <span className="visually-hidden">Cargando...</span>
                                </div>
                                <span className="text-muted">Cargando lectoras...</span>
                              </div>
                            </td>
                          </tr>
                        ) : lectoresFiltrados.length === 0 ? (
                          <tr>
                            <td colSpan="3" className="text-center py-5">
                              <Alert variant="info" className="mb-0">
                                <FaBarcode className="me-2" />
                                No se encontraron lectoras
                              </Alert>
                            </td>
                          </tr>
                        ) : (
                          lectoresFiltrados.map((lector) => (
                            <tr 
                              key={lector.id}
                              className={state.lectorSeleccionado?.id === lector.id ? 'selected' : ''}
                              style={{ cursor: 'pointer' }}
                              onClick={() => handleLectorSelect(lector)}
                            >
                              <td onClick={(e) => e.stopPropagation()}>
                                <Form.Check
                                  type="checkbox"
                                  className="dh-checkbox"
                                  checked={state.lectorSeleccionado?.id === lector.id}
                                  onChange={(e) => {
                                    e.stopPropagation();
                                    handleLectorSelect(lector);
                                  }}
                                />
                              </td>
                              <td style={{ fontWeight: '600', color: '#374151' }}>{lector.nombre}</td>
                              <td className="text-center">
                                {lector.activo ? (
                                  <Badge className="dh-badge-success">
                                    <FaCheck /> Activo
                                  </Badge>
                                ) : (
                                  <Badge className="dh-badge-danger">
                                    <FaTimes /> Inactivo
                                  </Badge>
                                )}
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </Table>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      <ModalLector
        show={showModal}
        onHide={handleCloseModal}
        onSave={handleSaveLector}
        lector={lectorToEdit}
        comedores={comedores}
      />

      <Modal
        title="Confirmar eliminación"
        open={showDeleteModal}
        onOk={confirmDelete}
        onCancel={cancelDelete}
        okText="Eliminar"
        cancelText="Cancelar"
        okType="danger"
        style={{ top: 'auto', bottom: 20 }}
        getContainer={() => document.body}
      >
        <p>¿Estás seguro de que quieres eliminar el lector "{lectorToDelete?.nombre}"?</p>
        <p>Esta acción no se puede deshacer.</p>
      </Modal>
    </>
  );
};

export default CargarLectoras;